import {
  createSlice, PayloadAction, createAsyncThunk,
} from '@reduxjs/toolkit';
import { doGetRequest, fetchEventsWithLoop } from '../../common/request';
import { EnumDeviceSearchType, IDeviceSearchParams, IEventSearchParams } from '../../common/models';
import { appConfig } from '../../common/config';
import { RootState } from '../../store';

interface CustomerState {
  customers: any[];
  devices: any[];
  events: any[];
  deviceTypeNames: string[];
  isLoadingCustomers: boolean;
  isLoadingDevices: boolean;
  isLoadingEvents: boolean;
  mappedCustomers: { [key: string]: any };
}

const initialState: CustomerState = {
  customers: [],
  devices: [],
  events: [],
  deviceTypeNames: [],
  isLoadingCustomers: false,
  isLoadingDevices: false,
  isLoadingEvents: false,
  mappedCustomers: {},
};

// Handle actions in your reducers
const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    setLoadingCustomers: (state) => {
      state.isLoadingCustomers = true;
      state.customers = [];
    },
    setLoadingDevices: (state) => {
      state.isLoadingDevices = true;
      state.devices = [];
      state.deviceTypeNames = [];
    },
    setLoadingEvents: (state) => {
      state.isLoadingEvents = true;
      state.events = [];
    },
    setCustomers: (state, action: PayloadAction<any[]>) => {
      state.isLoadingCustomers = false;
      state.customers = action.payload;
    },
    setDevices: (state, action: PayloadAction<{devices: any[], deviceTypeNames: string[]}>) => {
      state.isLoadingDevices = false;
      state.devices = action.payload.devices;
      state.deviceTypeNames = action.payload.deviceTypeNames;
    },
    setEvents: (state, action: PayloadAction<any[]>) => {
      state.isLoadingEvents = false;
      state.events = action.payload;
    },
    setCustomerDeviceCount(state, action: PayloadAction<{customerId: string, deviceCount: number}>) {
      state.mappedCustomers[action.payload.customerId] = action.payload.deviceCount;
    },
    setLoadingDevicesError: (state) => {
      state.isLoadingDevices = false;
    },
    setLoadingEventsError: (state) => {
      state.isLoadingEvents = false;
    },
    clearData: (state) => {
      state.customers = [];
      state.devices = [];
      state.events = [];
      state.deviceTypeNames = [];
      state.isLoadingCustomers = false;
      state.isLoadingDevices = false;
      state.isLoadingEvents = false;
      state.mappedCustomers = {};
    }
  },
});

export const fetchCustomers = createAsyncThunk<void>(
  'customer/fetchCustomers',
  async (_, thunkAPI) => {
    try {
      if ((thunkAPI.getState() as RootState).customer.customers.length  > 0) {
        // No need to fetch customers again if we already have them
        return;
      }
      thunkAPI.dispatch(customerSlice.actions.setLoadingCustomers());
      const customers = await doGetRequest(`${appConfig.settings.pipApiBase}/customers`);
      const sortedCustomers = customers.filter((a: any) => a.Name).sort((a: any, b: any) => a.Name.localeCompare(b.Name));
      thunkAPI.dispatch(customerSlice.actions.setCustomers(sortedCustomers));
    } catch (err) {
      console.error(err);
    }
  },
);

export const fetchDeviceCount = createAsyncThunk<void, {customerId: string}>(
  'customer/fetchDeviceCount',
  async (params, thunkAPI) => {
    try {
      const devices = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${params.customerId}/devices`);
      thunkAPI.dispatch(customerSlice.actions.setCustomerDeviceCount({customerId: params.customerId, deviceCount: devices ? devices.length : 0}));
    } catch (err) {
      console.error(err);
    }
  },
);

export const fetchDevices = createAsyncThunk<void, IDeviceSearchParams>(
  'customer/fetchDevices',
  async (params, thunkAPI) => {
    try {
      if (!params.keywords) {
        return;
      }
      thunkAPI.dispatch(customerSlice.actions.setLoadingDevices());
      let devices: any[] = [];
      if (params.searchType === EnumDeviceSearchType.customerId) {
        // Search by customer ID
        devices = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${params.keywords}/devices`, false) ?? [];
      } else if(params.searchType === EnumDeviceSearchType.deviceIdSerial) {
        // Search by device ID or serial number
        const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/${params.keywords}`, false);
        devices = device ? [device] : [];
      } else if (params.searchType === EnumDeviceSearchType.customerName) {
        // Serach by customer name
        const customers = (thunkAPI.getState() as RootState).customer.customers.filter((customer) => customer.Name === params.keywords);
        if (customers.length === 0) {
          return;
        }
        devices = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customers[0].CustomerId}/devices`, false) ?? [];
      } else if (params.searchType === EnumDeviceSearchType.networkId) {
        // Search by device network ID
        const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/networkId/${params.keywords}`, false) ?? [];
        devices = device ? [device] : [];
      }
      
      const filteredDevices: any[] = [];
      const deviceTypeNames: string[] = [];
      for (const device of devices) {
        filteredDevices.push(device);
        if (!deviceTypeNames.includes(device.DeviceTypeName)) {
          deviceTypeNames.push(device.DeviceTypeName);
        }
      }
      thunkAPI.dispatch(customerSlice.actions.setDevices({devices: filteredDevices, deviceTypeNames: deviceTypeNames.sort()}));
    } catch (err) {
      thunkAPI.dispatch(customerSlice.actions.setLoadingDevicesError());
      console.error(err);
    }
  },
);

// TODO: Let’s keep it in case we need it later.
// export const fetchEvents = createAsyncThunk<void, IEventSearchParams>(
//   'customer/fetchEvents',
//   async (params, thunkAPI) => {
//     try {
//       if (!params.keywords) {
//         return;
//       }
//       thunkAPI.dispatch(customerSlice.actions.setLoadingEvents());

//       let events: any[] = [];
//       let timeRangeQuery = '';
//       if (params.startTime && params.endTime) {
//         timeRangeQuery = `&fromUtc=${encodeURIComponent(params.startTime)}&toUtc=${encodeURIComponent(params.endTime)}`;
//       }

//       if (params.searchType === EnumDeviceSearchType.customerId) {
//         // Search by customer ID
//         events = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${params.keywords}/events?${timeRangeQuery}`, false) ?? [];
//       } else if(params.searchType === EnumDeviceSearchType.deviceIdSerial) {
//         // Search by device ID or serial number
//         const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/${params.keywords}`, false);
//         if (!device) {
//           return;
//         }
//         events = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${device.CustomerId}/events?deviceIds=${device.DeviceId}${timeRangeQuery}`, false) ?? [];
//       } else if (params.searchType === EnumDeviceSearchType.customerName) {
//         // Serach by customer name
//         const customers = (thunkAPI.getState() as RootState).customer.customers.filter((customer) => customer.Name === params.keywords);
//         if (customers.length === 0) {
//           return;
//         }
//         events = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${customers[0].CustomerId}/events?${timeRangeQuery}`, false) ?? [];
//       } else if (params.searchType === EnumDeviceSearchType.networkId) {
//         // Search by device network ID
//         const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/networkId/${params.keywords}`, false) ?? [];
//         if (!device) {
//           return;
//         }
//         events = await doGetRequest(`${appConfig.settings.pipApiBase}/customers/${device.CustomerId}/events?deviceIds=${device.DeviceId}`, false) ?? [];
//       }
//       thunkAPI.dispatch(customerSlice.actions.setEvents(events));
//     } catch (err) {
//       thunkAPI.dispatch(customerSlice.actions.setLoadingEventsError());
//       console.error(err);
//     }
//   },
// );

export const fetchEvents = createAsyncThunk<void, IEventSearchParams>(
  'customer/fetchEvents',
  async (params, thunkAPI) => {
    try {
      if (!params.keywords) {
        return;
      }
      thunkAPI.dispatch(customerSlice.actions.setLoadingEvents());

      let events: any[] = [];
      if (params.searchType === EnumDeviceSearchType.customerId) {
        // Search by customer ID
        events = await fetchEventsWithLoop(`${appConfig.settings.pipApiBase}/customers/${params.keywords}/events?`, params.startTime, params.endTime) ?? [];
      } else if(params.searchType === EnumDeviceSearchType.deviceIdSerial) {
        // Search by device ID or serial number
        const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/${params.keywords}`, false);
        if (!device) {
          return;
        }
        events = await fetchEventsWithLoop(`${appConfig.settings.pipApiBase}/customers/${device.CustomerId}/events?deviceIds=${device.DeviceId}`, params.startTime, params.endTime) ?? [];
      } else if (params.searchType === EnumDeviceSearchType.customerName) {
        // Serach by customer name
        const customers = (thunkAPI.getState() as RootState).customer.customers.filter((customer) => customer.Name === params.keywords);
        if (customers.length === 0) {
          return;
        }
        events = await fetchEventsWithLoop(`${appConfig.settings.pipApiBase}/customers/${customers[0].CustomerId}/events?`, params.startTime, params.endTime) ?? [];
      } else if (params.searchType === EnumDeviceSearchType.networkId) {
        // Search by device network ID
        const device = await doGetRequest(`${appConfig.settings.pipApiBase}/devices/networkId/${params.keywords}`, false) ?? [];
        if (!device) {
          return;
        }
        events = await fetchEventsWithLoop(`${appConfig.settings.pipApiBase}/customers/${device.CustomerId}/events?deviceIds=${device.DeviceId}`, params.startTime, params.endTime) ?? [];
      }
      thunkAPI.dispatch(customerSlice.actions.setEvents(events));
    } catch (err) {
      thunkAPI.dispatch(customerSlice.actions.setLoadingEventsError());
      console.error(err);
    }
  },
);

export const {
  clearData,
} = customerSlice.actions;
export default customerSlice.reducer;
